import React, { useEffect, useRef } from "react";
import {
  BoldExtension,
  ItalicExtension,
  UnderlineExtension,
  FontSizeExtension,
  TextColorExtension,
  HeadingExtension,
  BulletListExtension,
  HardBreakExtension,
  LinkExtension,
  OrderedListExtension,
  ImageExtension,
  SubExtension,
  SupExtension,
  TextHighlightExtension
} from "remirror/extensions";
import { TableExtension } from "@remirror/extension-react-tables";
import {
  EditorComponent,
  Remirror,
  useRemirror,
  ReactComponentExtension,
} from "@remirror/react";
import "remirror/styles/all.css";
import { EditorMenu } from "../EditorMenu";

export const WYSIWYGEditor = ({ initialContent = '', handleChange }) => {
  const stripSingleParagraphWrapper = (html) => {
    if (!html) return "";

    const container = document.createElement("div");
    container.innerHTML = html.trim();

    // Remove empty text nodes so we can reliably inspect the root element
    const meaningfulChildren = Array.from(container.childNodes).filter(
      (node) => !(node.nodeType === Node.TEXT_NODE && node.textContent.trim() === "")
    );

    if (
      meaningfulChildren.length === 1 &&
      meaningfulChildren[0].nodeName === "P"
    ) {
      return meaningfulChildren[0].innerHTML;
    }

    return html.trim();
  };

  const normalizedInitialContent = stripSingleParagraphWrapper(initialContent);
  const { manager, state, setState } = useRemirror({
    extensions: () => [
      new BoldExtension(),
      new ItalicExtension(),
      new UnderlineExtension(),
      new FontSizeExtension(),
      new TextColorExtension(),
      new HeadingExtension(),
      new BulletListExtension(),
      new OrderedListExtension(),
      new LinkExtension(),
      new HardBreakExtension(),
      new ReactComponentExtension(),
      new TableExtension(),
      new ImageExtension({ enableResizing: true }),
      new SubExtension(),
      new SupExtension(),
      new TextHighlightExtension(),
    ],
    selection: "start",
    stringHandler: "html",
    content: normalizedInitialContent,
  });

  const contentRef = useRef(normalizedInitialContent);

  useEffect(() => {
    const sanitized = stripSingleParagraphWrapper(initialContent);
    if (sanitized !== contentRef.current) {
      setState(manager.createState({ content: sanitized, selection: "end" }));
      contentRef.current = sanitized; // Prevent unnecessary re-renders
    }
  }, [initialContent, manager, setState]);

  const onChange = (parameter) => {
    setState(parameter.state);
    const html = parameter.helpers.getHTML();
    const sanitized = stripSingleParagraphWrapper(html);
    contentRef.current = sanitized;
    handleChange(sanitized);
  };

  return (
    <div className="remirror-theme">
      <Remirror manager={manager} state={state} onChange={onChange}>
        <EditorMenu />
        <EditorComponent />
      </Remirror>
    </div>
  );
};
